import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { ReactNode } from 'react';

import { useTrackItemCardClicked } from '@/utils/amplitude/analytics';

import type { CoverLotData } from '../types';

import { CoverLotImage, LARGE_IMAGE_SIZE, THUMB_IMAGE_SIZE } from '../CoverLotImage';
import { CountdownBadge } from './CountdownBadge';

type Props = {
    completionTime: number;
    countdownLabel: ReactNode;
    coverLots: CoverLotData[];
    doneOverlay: ReactNode;
    hideCountdown?: boolean;
    onCountdownComplete?: () => void;
    trackItemCardClicked: ReturnType<typeof useTrackItemCardClicked>;
};

export const DesktopImageCluster = ({
    completionTime,
    countdownLabel,
    coverLots,
    doneOverlay,
    hideCountdown = false,
    onCountdownComplete,
    trackItemCardClicked,
}: Props) => {
    const [primaryLot, ...thumbLots] = coverLots;
    const hasThumbColumn = thumbLots.length > 0;

    return (
        <div className="relative flex rounded-md border border-border-secondary bg-background-primary">
            <div className="relative flex overflow-hidden rounded-md">
                <div
                    className={cn(
                        'flex h-[256px] w-[256px] shrink-0 items-center justify-center overflow-hidden',
                        hasThumbColumn && 'border-r border-border-secondary'
                    )}
                >
                    <CoverLotImage
                        alt={primaryLot.title}
                        catalogId={primaryLot.catalogId}
                        href={primaryLot.url}
                        imageSize={LARGE_IMAGE_SIZE}
                        itemId={primaryLot.itemId}
                        promoted={primaryLot.promoted}
                        src={primaryLot.image}
                        srcSet={`${primaryLot.imageHighDpi} 2x`}
                        trackItemCardClicked={trackItemCardClicked}
                    />
                </div>
                {hasThumbColumn && (
                    <div className="flex flex-col">
                        {thumbLots.map((lot, index) => (
                            <div
                                key={lot.itemId}
                                className={cn(
                                    'flex h-[128px] w-[128px] items-center justify-center overflow-hidden',
                                    index < thumbLots.length - 1 && 'border-b border-border-secondary'
                                )}
                            >
                                <CoverLotImage
                                    alt={lot.title}
                                    catalogId={lot.catalogId}
                                    href={lot.url}
                                    imageSize={THUMB_IMAGE_SIZE}
                                    itemId={lot.itemId}
                                    promoted={lot.promoted}
                                    src={lot.image}
                                    srcSet={`${lot.imageHighDpi} 2x`}
                                    trackItemCardClicked={trackItemCardClicked}
                                />
                            </div>
                        ))}
                    </div>
                )}
                {doneOverlay}
            </div>
            <CountdownBadge
                completionTime={completionTime}
                hideCountdown={hideCountdown}
                label={countdownLabel}
                onComplete={onCountdownComplete}
            />
        </div>
    );
};
